import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Avatar,
  Grid,
  List,
  ListItem,
  Divider,
  ListItemText,
  Collapse,
  ListSubheader,
  ButtonGroup,
  Button,
} from "@mui/material";
import { DateTime, Interval } from "luxon";

import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { useService } from "contexts/ServiceContextProvider";
import { useTranslation } from "react-i18next";
import "../../../css/components/Calendar/Events/ViewEventsModal.css";
import EditIcon from '@mui/icons-material/Edit';
import { NavigateBefore, NavigateNext, Today } from "@mui/icons-material";
import { convertToUTCISO } from 'components/Custom/TimezoneConvertion.jsx';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EventDetailsModal from '../Events/EventsDetailsModal';
import ScrollAwareTooltip from "components/Custom/ScrollAwareTooltip";

const ViewEventsModal = ({ open, onClose, workspaceId, userTimezone }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [descriptionVisibility, setDescriptionVisibility] = useState({});
  const { getWorkspaceEvents, updateWorkspaceEvent, deleteWorkspaceEvent } = useService();

  const workspaceContext = useWorkspaces();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(DateTime.local().startOf('month'));
  const [viewType,] = useState('month'); // 'month' or 'week'
  const [eventsByDate, setEventsByDate] = useState({});

  const [myUserPerms, setMyUserPerms] = useState(null);
  const [editEventModalOpen, setEditEventModalOpen] = useState(false);

  const openEditEvent = (rawEvent) => {
    const fullCalStyleEvent = {
      id: rawEvent.id,
      title: rawEvent.title,
      allDay: rawEvent.allDay,
      start: rawEvent.originalStart,
      end: rawEvent.originalEnd,
      backgroundColor: rawEvent.backgroundColor,
      extendedProps: {
        description: rawEvent.description,
        collaborators: rawEvent.collaborators,
        visibility: rawEvent.visibility,
      }
    }
    setSelectedEvent(fullCalStyleEvent);
    setEditEventModalOpen(true);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!workspaceContext.loading && workspaceContext.workspaceWorkers.length > 0) {
      const userPerms = workspaceContext.getLoggedUserPermissions();
      setMyUserPerms(userPerms);
    }
  }, [workspaceContext.loading, workspaceContext.workspaceWorkers]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (open) {
      const fetchEvents = async () => {
        try {
          const eventsData = await getWorkspaceEvents(workspaceId);
          const formattedEvents = eventsData.map((event) => {
            return {
              id: event.id,
              title: event.title,
              allDay: event.all_day_event,
              description: event.description,
              backgroundColor: event.color_code,
              collaborators: event.collaborators,
              originalStart: event.date_start,
              originalEnd: event.date_end,
              creator: event.creator || "" //because old ones might crash since they didn't have creators
            };
          });
          setEvents(formattedEvents);
        } catch (error) {
          console.error("Error fetching events:", error);
        }
      };
      fetchEvents();
    }
  }, [open, getWorkspaceEvents, workspaceId]);

  // Filter and group events whenever events or currentDate changes
  useEffect(() => {
    if (events.length > 0) {
      const startOfRange = currentDate.startOf(viewType);
      const endOfRange = currentDate.endOf(viewType);

      const filteredEvents = events.filter((event) => {
        const eventStart = event.allDay
          ? DateTime.fromISO(event.originalStart, { zone: 'utc' })
          : DateTime.fromISO(event.originalStart, { zone: userTimezone });

        return Interval.fromDateTimes(startOfRange, endOfRange).contains(eventStart);
      });

      // Group events by date
      const groupedEvents = filteredEvents.reduce((acc, event) => {
        const eventStart = event.allDay
          ? DateTime.fromISO(event.originalStart, { zone: 'utc' })
          : DateTime.fromISO(event.originalStart, { zone: userTimezone });

        const dateKey = eventStart.toFormat('yyyy-MM-dd');
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(event);
        return acc;
      }, {});

      setEventsByDate(groupedEvents);
    } else {
      setEventsByDate({});
    }
  }, [events, currentDate, viewType, userTimezone]);

  const toggleDescriptionVisibility = (eventId) => {
    setDescriptionVisibility((prevState) => ({
      ...prevState,
      [eventId]: !prevState[eventId],
    }));
  };

  const handlePrev = () => {
    setCurrentDate((prevDate) => prevDate.minus({ [viewType]: 1 }));
  };

  const handleNext = () => {
    setCurrentDate((prevDate) => prevDate.plus({ [viewType]: 1 }));
  };

  const handleToday = () => {
    setCurrentDate(DateTime.local().startOf(viewType));
  };

  const handleEventDelete = async (event_id, workspace_id) => {
    await deleteWorkspaceEvent(event_id, workspace_id)
    setEvents(prevEvents => prevEvents.filter(event => event.id !== event_id))
    setEditEventModalOpen(false);
  }

  const handleEventSave = async (editableEvent) => {
    try {
      const updatedEvent = {
        ...editableEvent,
        collaborators: editableEvent.collaborators.map(c => c.id),
        date_start: editableEvent.all_day_event
          ? DateTime.fromISO(editableEvent.date_start).toUTC().toISODate()
          : convertToUTCISO(editableEvent.date_start, userTimezone),
        date_end: editableEvent.all_day_event
          ? DateTime.fromISO(editableEvent.date_end).plus({ days: 1 }).toUTC().toISODate()
          : convertToUTCISO(editableEvent.date_end, userTimezone),
      };
      await updateWorkspaceEvent(updatedEvent, workspaceId, updatedEvent.id);

      setEvents(prevEvents => {
        return prevEvents.map(event => {
          if (event.id === updatedEvent.id) {
            return {
              ...event,
              title: editableEvent.title,
              creator: editableEvent.creator,
              originalStart: editableEvent.all_day_event
                ? DateTime.fromISO(editableEvent.date_start).toUTC().toISODate()
                : DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).toUTC().toISO(),
              originalEnd: editableEvent.all_day_event
                ? DateTime.fromISO(editableEvent.date_end).plus({ days: 1 }).toUTC().toISODate()
                : DateTime.fromISO(editableEvent.date_end, { zone: userTimezone }).toUTC().toISO(),
              allDay: editableEvent.all_day_event,
              backgroundColor: editableEvent.colorCode,

              description: editableEvent.description,
              collaborators: editableEvent.collaborators,
              visibility: editableEvent.visibility

            };
          }
          return event;
        });
      });

      setEditEventModalOpen(false);
    } catch (error) {
      console.error("Failed to update event:", error);
    }
  };

  return (
    <>
      {editEventModalOpen && (
        <EventDetailsModal
          open={editEventModalOpen}
          onClose={() => setEditEventModalOpen(false)}
          selectedEvent={selectedEvent}
          myUserPerms={myUserPerms}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
          workspaceId={workspaceId}
          userTimezone={userTimezone}
          autoEditOnOpen={true}
        />
      )}
      <Modal open={open} onClose={onClose}>
        <Box className="view-events-main-styles view-events-modal-wrapper custom-list-item-styling">
          <Typography variant="h5" gutterBottom style={{ color: "#e0e0e0" }}>
            {t("calendar.event_view")}
          </Typography>

          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            mb={2}
            style={{ color: "#e0e0e0" }}
          >
            <ButtonGroup variant="outlined">
              <Button style={{ color: "#e0e0e0", backgroundColor: "#333333" }} onClick={handlePrev}>
                <NavigateBefore />
              </Button>
              <Button style={{ color: "#e0e0e0", backgroundColor: "#333333" }} onClick={handleToday}>
                <Today />
              </Button>
              <Button style={{ color: "#e0e0e0", backgroundColor: "#333333" }} onClick={handleNext}>
                <NavigateNext />
              </Button>
            </ButtonGroup>
            <Typography variant="h6" sx={{ color: "#e0e0e0" }}>
              {currentDate.setLocale(t("global.lng")).toLocaleString({ month: "long", year: "numeric" })}
            </Typography>
          </Box>

          <List>
            {Object.keys(eventsByDate).length === 0 ? (
              <Typography
                variant="h4"
                sx={{ color: "#b0b0b0", textAlign: "center", marginTop: "20px" }}
              >
                {t("calendar.no_events")}
              </Typography>
            ) : (
              Object.keys(eventsByDate)
                .sort()
                .map((dateKey) => {
                  const dateEvents = eventsByDate[dateKey];
                  const formattedDate = DateTime.fromISO(dateKey, { locale: t("global.lng") }).toLocaleString(DateTime.DATE_FULL);


                  return (
                    <React.Fragment key={dateKey}>
                      <div style={{ marginTop: "50px" }}>
                        <ListSubheader disableSticky className="custom-list-subheader">
                          {formattedDate}
                        </ListSubheader>
                      </div>
                      {dateEvents.map((event) => {
                        // Define eventStart and eventEnd inside the map function
                        const eventStart = event.allDay
                          ? DateTime.fromISO(event.originalStart, { zone: 'utc' })
                          : DateTime.fromISO(event.originalStart, { zone: userTimezone });

                        const eventEnd = event.allDay
                          ? DateTime.fromISO(event.originalEnd, { zone: 'utc' })
                          : DateTime.fromISO(event.originalEnd, { zone: userTimezone });

                        return (
                          <React.Fragment key={event.id}>
                            <ListItem alignItems="flex-start">
                              <ListItemText
                                primary={
                                  <Typography variant="h6" style={{ display: "flex", justifyContent: "space-between", color: event.backgroundColor || "#e0e0e0" }}>
                                    {event.title}
                                  </Typography>

                                }
                                
                                secondary={
                                  <>
                                    <Typography variant="body2" style={{ color: "#b0b0b0" }}>
                                      {event.allDay
                                        ? `${t("calendar.event_all_day")}`
                                        : eventStart.toFormat('yyyy-MM-dd') === eventEnd.toFormat('yyyy-MM-dd')
                                          ? `${eventStart.toFormat('HH:mm')} - ${eventEnd.toFormat('HH:mm')}`
                                          : `${eventStart.toFormat('yyyy-MM-dd HH:mm')} - ${eventEnd.toFormat('yyyy-MM-dd HH:mm')}`}
                                    </Typography>

                                    {event.collaborators.length > 0 && (
                                      <Grid
                                        item
                                        container
                                        spacing={1}
                                        className="view-events-collab-grid"
                                        sx={{
                                          marginTop: 1, // Ensure some space from the description
                                          flexWrap: "wrap", // Allow wrapping of avatars
                                          justifyContent: "flex-start", // Align avatars to the left
                                        }}
                                      >
                                        {event.collaborators.map((collaborator) => (
                                          <Grid item key={collaborator.id}>
                                            <ScrollAwareTooltip
                                              title={collaborator.full_name}
                                              arrow
                                              className="view-events-collab-tooltip"
                                            >
                                              <Avatar
                                                src={collaborator.image}
                                                alt={collaborator.full_name}
                                                className="view-events-avatar"
                                              />
                                            </ScrollAwareTooltip>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    )}
                                    <Box sx={{ display: "flex", gap: "10px", color: "lightblue" }}>
                                      <Typography variant="body2" style={{ color: "#b0b0b0", marginTop: "4px" }}>
                                        {event.collaborators.length} {t("modal.participants")}
                                      </Typography>
                                      {(myUserPerms?.is_admin || myUserPerms?.is_owner || event.creator === myUserPerms?.user_id) && (
                                        <Box sx={{ display: "flex", cursor: "pointer", alignItems: "center"}} onClick={() => openEditEvent(event)} >
                                          <EditIcon
                                            sx={{ marginTop: "2px", marginRight:"8px" }}
                                          />
                                          <Typography style={{ marginTop: "4px", marginBottom: "4px" }}>
                                            {t("calendar.event_edit_title")}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>

                                    <Typography
                                      sx={{ display: "flex", alignItems: "center", color: "#e0e0e0", cursor: "pointer" }}
                                      onClick={() => toggleDescriptionVisibility(event.id)}
                                    >
                                      {t("modal.description")}
                                      {descriptionVisibility[event.id] ? (
                                        <VisibilityIcon sx={{ ml: 1 }} />
                                      ) : (
                                        <VisibilityOffIcon sx={{ ml: 1 }} />
                                      )}
                                    </Typography>
                                  </>
                                }
                              />



                            </ListItem>
                            {event.description && (
                              <Collapse
                                in={descriptionVisibility[event.id]}
                                timeout="auto"
                                unmountOnExit
                                style={{ backgroundColor: "#292929" }}
                              >
                                <Box className="view-events-description" sx={{ marginLeft: 4, marginRight: 4 }}>
                                  <Typography variant="body1" sx={{ display: "flex", justifyContent: "center" }}>
                                    {t("modal.description")}
                                  </Typography>
                                  <Typography variant="body2" sx={{ overflowWrap: "break-word" }} >{event.description}</Typography>
                                </Box>
                              </Collapse>
                            )}

                            <Divider component="li" sx={{ backgroundColor: "#C0C0C0" }} />
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })
            )}
          </List>
        </Box>

      </Modal>
    </>
  );
};

export default ViewEventsModal;
